import './vcf_upload.style.scss';

import { InboxOutlined } from '@ant-design/icons';
import { Button, notification, Tabs, Typography, Upload } from 'antd';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Modal from '../../foundation/components/modal/Modal';
import { useModalForm } from '../../foundation/hooks';
import {
  useUploadVarvisMutation,
  useUploadVcfMutation,
} from '../api/variantApi';
import { VCF_UPLOAD_NOTIFICATION_MESSAGES } from './constants';

const VARVIS_ACCEPTED_EXTENSIONS = '.csv,.xlsx';

const UPLOAD_MODE_TABS = {
  VCF: 'vcf',
  VARVIS: 'varvis',
} as const;

const VcfUploadPage = () => {
  const navigate = useNavigate();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [varvisFileList, setVarvisFileList] = useState<UploadFile[]>([]);
  const [selectedVarvisFile, setSelectedVarvisFile] = useState<File | null>(
    null,
  );
  const [uploadVcf] = useUploadVcfMutation();
  const [uploadVarvis] = useUploadVarvisMutation();

  const closeModal = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const { isLoading, setIsLoading, createModalOpts } = useModalForm({
    onClose: closeModal,
    requireWorkflowEdit: false,
  });

  const handleBeforeUpload: UploadProps['beforeUpload'] = useCallback(
    (file: RcFile) => {
      setSelectedFile(file);
      setFileList([
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
          originFileObj: file,
        },
      ]);
      return Upload.LIST_IGNORE;
    },
    [],
  );

  const handleRemove = useCallback(() => {
    setSelectedFile(null);
    setFileList([]);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedFile) {
      notification.error({
        message: VCF_UPLOAD_NOTIFICATION_MESSAGES.UPLOAD_FAILED,
        description: VCF_UPLOAD_NOTIFICATION_MESSAGES.FILE_REQUIRED,
      });
      return;
    }

    const formData = new FormData();
    formData.append('vcf_file', selectedFile, selectedFile.name);

    try {
      setIsLoading(true);
      const response = await uploadVcf(formData).unwrap();
      notification.success({
        message: VCF_UPLOAD_NOTIFICATION_MESSAGES.UPLOAD_STARTED,
        description: response.message,
      });
      navigate('/running-tasks');
    } catch {
      notification.error({
        message: VCF_UPLOAD_NOTIFICATION_MESSAGES.UPLOAD_FAILED,
      });
    } finally {
      setIsLoading(false);
    }
  }, [navigate, selectedFile, setIsLoading, uploadVcf]);

  const handleVarvisBeforeUpload: UploadProps['beforeUpload'] = useCallback(
    (file: RcFile) => {
      setSelectedVarvisFile(file);
      setVarvisFileList([
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
          originFileObj: file,
        },
      ]);
      return Upload.LIST_IGNORE;
    },
    [],
  );

  const handleVarvisRemove = useCallback(() => {
    setSelectedVarvisFile(null);
    setVarvisFileList([]);
  }, []);

  const handleVarvisConfirm = useCallback(async () => {
    if (!selectedVarvisFile) {
      notification.error({
        message: VCF_UPLOAD_NOTIFICATION_MESSAGES.VARVIS_UPLOAD_FAILED,
        description: VCF_UPLOAD_NOTIFICATION_MESSAGES.VARVIS_FILE_REQUIRED,
      });
      return;
    }

    const formData = new FormData();
    formData.append('varvis_file', selectedVarvisFile, selectedVarvisFile.name);

    try {
      setIsLoading(true);
      const response = await uploadVarvis(formData).unwrap();
      notification.success({
        message: VCF_UPLOAD_NOTIFICATION_MESSAGES.VARVIS_UPLOAD_STARTED,
        description: response.message,
      });
      navigate('/running-tasks');
    } catch {
      notification.error({
        message: VCF_UPLOAD_NOTIFICATION_MESSAGES.VARVIS_UPLOAD_FAILED,
      });
    } finally {
      setIsLoading(false);
    }
  }, [navigate, selectedVarvisFile, setIsLoading, uploadVarvis]);

  return (
    <>
      <Modal
        {...createModalOpts('Upload variants')}
        className="vcf-upload-modal"
        confirmLoading={isLoading}
        footer={null}
      >
        <Tabs
          defaultActiveKey={UPLOAD_MODE_TABS.VCF}
          items={[
            {
              key: UPLOAD_MODE_TABS.VCF,
              label: 'VCF',
              children: (
                <>
                  <Typography.Paragraph>
                    Select a VCF file to create its variants in independent
                    background jobs.
                  </Typography.Paragraph>
                  <Upload.Dragger
                    accept=".vcf,.vcf.gz"
                    beforeUpload={handleBeforeUpload}
                    disabled={isLoading}
                    fileList={fileList}
                    maxCount={1}
                    onRemove={handleRemove}
                    showUploadList={{ showRemoveIcon: !isLoading }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag a VCF file here
                    </p>
                    <p className="ant-upload-hint">
                      Only .vcf and .vcf.gz files are accepted.
                    </p>
                  </Upload.Dragger>
                  <div className="c-form__btn-wrapper vcf-upload-modal__actions">
                    <Button onClick={closeModal} disabled={isLoading}>
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      onClick={handleConfirm}
                      disabled={!selectedFile || isLoading}
                      loading={isLoading}
                    >
                      Confirm upload
                    </Button>
                  </div>
                </>
              ),
            },
            {
              key: UPLOAD_MODE_TABS.VARVIS,
              label: 'Varvis-Export',
              children: (
                <>
                  <Typography.Paragraph>
                    Select a Varvis CSV or XLSX export to create its variants in
                    independent background jobs.
                  </Typography.Paragraph>
                  <Upload.Dragger
                    accept={VARVIS_ACCEPTED_EXTENSIONS}
                    beforeUpload={handleVarvisBeforeUpload}
                    disabled={isLoading}
                    fileList={varvisFileList}
                    maxCount={1}
                    onRemove={handleVarvisRemove}
                    showUploadList={{ showRemoveIcon: !isLoading }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag a Varvis file here
                    </p>
                    <p className="ant-upload-hint">
                      Only .csv and .xlsx files are accepted.
                    </p>
                  </Upload.Dragger>
                  <div className="c-form__btn-wrapper vcf-upload-modal__actions">
                    <Button onClick={closeModal} disabled={isLoading}>
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      onClick={handleVarvisConfirm}
                      disabled={!selectedVarvisFile || isLoading}
                      loading={isLoading}
                    >
                      Confirm upload
                    </Button>
                  </div>
                </>
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
};

export default VcfUploadPage;
